import { useCallback, useEffect, useState } from 'react';
import { fetchBundleFloorLevelBySku } from '../services/bundleFloorAPI';
import type { BundleFloorLevel } from '../schemas/apiResponse.schemas';

export interface BundleFloorLevelState {
  /** Null while loading, and for any SKU that is not a bundle parent with a pick bin (GET 404s). */
  readonly floor: BundleFloorLevel | null;
  /** Merges a PATCH / action response, keeping loaded bins and BOM when the response omits them. */
  readonly applyFloorUpdate: (updated: BundleFloorLevel) => void;
}

/** Floor level for a SKU, or null when it is not a bundle parent with at least one pick bin. */
export function useBundleFloorLevel(sku: string): BundleFloorLevelState {
  const [floor, setFloorState] = useState<BundleFloorLevel | null>(null);

  useEffect(() => {
    let cancelled = false;
    setFloorState(null);
    const trimmed = sku.trim();
    if (!trimmed) {
      return undefined;
    }
    void (async (): Promise<void> => {
      try {
        const loaded = await fetchBundleFloorLevelBySku(trimmed);
        if (!cancelled) {
          setFloorState((loaded.bins ?? []).length > 0 ? loaded : null);
        }
      } catch {
        if (!cancelled) setFloorState(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sku]);

  const applyFloorUpdate = useCallback((updated: BundleFloorLevel): void => {
    setFloorState((prev) => (prev
      ? {
          ...prev,
          ...updated,
          bins: updated.bins ?? prev.bins,
          components: updated.components ?? prev.components,
        }
      : updated));
  }, []);

  return { floor, applyFloorUpdate };
}
